import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Trophy, Medal, Crown, Star, Gem, Heart } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const badgeConfig = {
  bronze: { color: 'bg-amber-700', icon: Medal, label: 'Bronze' },
  silver: { color: 'bg-gray-400', icon: Medal, label: 'Silver' },
  gold: { color: 'bg-yellow-500', icon: Star, label: 'Gold' },
  platinum: { color: 'bg-slate-300 text-slate-800', icon: Crown, label: 'Platinum' },
  diamond: { color: 'bg-cyan-400', icon: Gem, label: 'Diamond' }
};

const Leaderboard = () => {
  const { t, language } = useLanguage();
  const [leaders, setLeaders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLeaderboard();
  }, []);

  const fetchLeaderboard = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/leaderboard`);
      setLeaders(response.data);
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRankDisplay = (rank) => {
    switch (rank) {
      case 1:
        return <Crown className="h-8 w-8 text-yellow-500" />;
      case 2:
        return <Medal className="h-7 w-7 text-gray-400" />;
      case 3:
        return <Medal className="h-6 w-6 text-amber-600" />;
      default:
        return <span className="text-2xl font-bold text-stone-400">{rank}</span>;
    }
  };

  return (
    <div className="min-h-screen bg-stone-100 overflow-y-auto">
      <Navbar />
      
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-amber-100 mb-4">
            <Trophy className="h-8 w-8 text-amber-600" />
          </div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-stone-900 tracking-tight">
            {t('topDonors')}
          </h1>
          <p className="text-stone-600 mt-2 max-w-lg mx-auto">
            I donatori più generosi della nostra community
          </p>
        </motion.div>

        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-800"></div>
          </div>
        ) : leaders.length === 0 ? (
          <div className="text-center py-20">
            <Heart className="h-16 w-16 text-stone-300 mx-auto mb-4" />
            <h3 className="font-heading text-xl font-semibold text-stone-700 mb-2">
              Nessun donatore ancora
            </h3>
            <p className="text-stone-500">Sii il primo a donare!</p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Top 3 Special Cards */}
            {leaders.slice(0, 3).length > 0 && (
              <div className="grid md:grid-cols-3 gap-4 mb-8">
                {[leaders[1], leaders[0], leaders[2]].filter(Boolean).map((leader, i) => {
                  if (!leader) return null;
                  const actualRank = leader.rank;
                  const isFirst = actualRank === 1;
                  const badge = badgeConfig[leader.badge_level];
                  const BadgeIcon = badge.icon;
                  
                  return (
                    <motion.div
                      key={leader.user_id}
                      initial={{ opacity: 0, y: 30 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.1 }}
                      className={`bg-white rounded-2xl shadow-sm border border-stone-200 p-6 text-center ${
                        isFirst ? 'md:order-2 ring-2 ring-amber-500 ring-offset-2' : i === 0 ? 'md:order-1' : 'md:order-3'
                      }`}
                    >
                      <div className="flex justify-center mb-4">
                        {getRankDisplay(actualRank)}
                      </div>
                      <Link to={`/profile/${leader.user_id}`}>
                        <Avatar className={`h-20 w-20 mx-auto mb-4 ${isFirst ? 'ring-4 ring-amber-500/30' : ''}`}>
                          <AvatarImage src={leader.picture || `https://api.dicebear.com/9.x/lorelei/svg?seed=${leader.user_id}`} />
                          <AvatarFallback className="text-2xl">{leader.name[0]}</AvatarFallback>
                        </Avatar>
                      </Link>
                      <Link to={`/profile/${leader.user_id}`}>
                        <h3 className="font-heading text-lg font-semibold text-stone-900 hover:text-emerald-800 transition-colors">
                          {leader.name}
                        </h3>
                      </Link>
                      <Badge className={`${badge.color} mt-2`}>
                        <BadgeIcon className="h-3 w-3 mr-1" />
                        {badge.label}
                      </Badge>
                      <div className="mt-4 pt-4 border-t border-stone-100">
                        <p className="font-heading text-2xl font-bold text-emerald-800">
                          €{leader.total_donated.toFixed(2)}
                        </p>
                        <p className="text-sm text-stone-500">{leader.donations_count} {t('donations')}</p>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )}

            {/* Rest of Leaderboard */}
            {leaders.slice(3).map((leader, index) => {
              const badge = badgeConfig[leader.badge_level];
              const BadgeIcon = badge.icon;
              
              return (
                <motion.div
                  key={leader.user_id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: (index + 3) * 0.05 }}
                  className="bg-white rounded-2xl shadow-sm border border-stone-200 p-4 flex items-center gap-4 hover:border-emerald-500/30 transition-colors"
                >
                  <div className="w-12 text-center">
                    {getRankDisplay(leader.rank)}
                  </div>
                  <Link to={`/profile/${leader.user_id}`} className="flex items-center gap-3 flex-1">
                    <Avatar className="h-12 w-12">
                      <AvatarImage src={leader.picture || `https://api.dicebear.com/9.x/lorelei/svg?seed=${leader.user_id}`} />
                      <AvatarFallback>{leader.name[0]}</AvatarFallback>
                    </Avatar>
                    <div>
                      <h3 className="font-semibold text-stone-900 hover:text-emerald-800 transition-colors">
                        {leader.name}
                      </h3>
                      <Badge variant="secondary" className={`${badge.color} text-xs`}>
                        <BadgeIcon className="h-3 w-3 mr-1" />
                        {badge.label}
                      </Badge>
                    </div>
                  </Link>
                  <div className="text-right">
                    <p className="font-heading text-xl font-bold text-emerald-800">
                      €{leader.total_donated.toFixed(2)}
                    </p>
                    <p className="text-sm text-stone-500">{leader.donations_count} {t('donations')}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default Leaderboard;
